import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * DATABASE BACKUP card — Admin Diagnostics.
 *
 * Chad runs everything from buttons, never a console. Two buttons: take a
 * backup now, and rehearse a restore. The age turns RED past 48 hours because a
 * backup that silently stops is worse than none — it is believed.
 */
const fmtBytes = (n) => (!n ? '—' : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${(n / 1e3).toFixed(0)} kB`);

const ageHours = (iso) => {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 3600000;
};

export const BackupCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [log, setLog] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/backup/status`);
      setData(r.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || 'could not read backup status' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (what) => {
    setBusy(what);
    setLog('');
    try {
      const r = await axios.post(`${API}/api/admin/backup/${what}`, {}, { timeout: 600000 });
      const res = r.data.result || {};
      if (what === 'drill') {
        setLog(res.log || '');
        (res.status === 'ok' ? toast.success : toast.error)(
          res.status === 'ok' ? `Restore drill PASSED in ${res.total_seconds}s`
                              : `Restore drill FAILED — ${(res.mismatches || []).length} finding(s)`);
      } else {
        toast.success(`Backup written — ${res.documents} documents, ${fmtBytes(res.stored_bytes)}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${what} failed`);
    } finally {
      setBusy('');
    }
  };

  if (!data) {
    return <div className="p-4 rounded-lg border border-slate-200 bg-white text-sm text-slate-500"
                data-testid="backup-card-loading">Reading backup status…</div>;
  }

  const latest = data.latest;
  const drill = data.last_drill;
  const attempt = data.last_attempt;
  const age = latest ? ageHours(latest.finished_at) : null;
  const stale = age === null || age > (data.policy?.stale_hours ?? 48);
  const failedSince = attempt && attempt.status === 'failed'
    && (!latest || attempt.id > latest.id);
  const tone = (!latest || stale || failedSince) ? 'border-red-300 bg-red-50'
    : (!drill || drill.status !== 'ok') ? 'border-amber-300 bg-amber-50'
    : 'border-emerald-300 bg-emerald-50';

  return (
    <div className={`p-5 rounded-xl border ${tone}`} data-testid="backup-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">Database backup</h3>
          <p className="text-xs text-slate-600 mt-0.5">
            {data.policy?.schedule}. {data.policy?.retention}. Encrypted with AES-256-GCM.
            The app cannot delete these — object storage has no delete verb.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => act('run')} disabled={!!busy}
                  data-testid="backup-run-btn"
                  className="px-3 py-1.5 text-xs rounded-md bg-slate-900 text-white disabled:opacity-40">
            {busy === 'run' ? 'Backing up…' : 'Back up now'}
          </button>
          <button type="button" onClick={() => act('drill')} disabled={!!busy}
                  data-testid="backup-drill-btn"
                  className="px-3 py-1.5 text-xs rounded-md border border-slate-400 text-slate-800 disabled:opacity-40">
            {busy === 'drill' ? 'Restoring…' : 'Run restore drill'}
          </button>
        </div>
      </div>

      {!latest && (
        <p className="text-sm font-medium text-red-800" data-testid="backup-none">
          No successful backup exists.
        </p>
      )}

      {latest && (
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs" data-testid="backup-facts">
          <div>
            <dt className="text-slate-500">Last successful</dt>
            <dd className={`font-mono ${stale ? 'text-red-700 font-semibold' : 'text-slate-900'}`}
                data-testid="backup-age">
              {age < 1 ? `${Math.round(age * 60)} min ago` : `${age.toFixed(1)} h ago`}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Size (encrypted)</dt>
            <dd className="font-mono text-slate-900" data-testid="backup-size">{fmtBytes(latest.stored_bytes)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Documents</dt>
            <dd className="font-mono text-slate-900" data-testid="backup-docs">
              {latest.documents?.toLocaleString()} in {latest.collections} collections
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">Restore drill</dt>
            <dd className="font-mono text-slate-900" data-testid="backup-drill-state">
              {drill ? `${drill.status === 'ok' ? 'PASS' : 'FAIL'} · ${drill.restore_seconds}s` : 'never run'}
            </dd>
          </div>
          <div className="col-span-2 md:col-span-4">
            <dt className="text-slate-500">Object key · sha256 of the gzip</dt>
            <dd className="font-mono text-xs break-all text-slate-700" data-testid="backup-key">
              {latest.key} · {latest.sha256}
            </dd>
          </div>
        </dl>
      )}

      {failedSince && (
        <p className="mt-3 text-xs text-red-800" data-testid="backup-last-error">
          Last attempt FAILED: {attempt.error}
        </p>
      )}

      {log && (
        <pre className="mt-3 p-3 bg-slate-900 text-slate-100 text-xs rounded-md overflow-x-auto max-h-72"
             data-testid="backup-drill-log">{log}</pre>
      )}

      {!log && drill?.log && (
        <details className="mt-3" data-testid="backup-drill-details">
          <summary className="text-xs text-slate-600 cursor-pointer">
            Last drill log — {new Date(drill.at).toLocaleString()} (show a customer)
          </summary>
          <pre className="mt-2 p-3 bg-slate-900 text-slate-100 text-xs rounded-md overflow-x-auto max-h-72">
            {drill.log}
          </pre>
        </details>
      )}
    </div>
  );
};

export default BackupCard;
